import * as THREE from 'three';
import {isSketchSlotObject, type SketchSlotObject} from '../../mesh/sketch';
import {isSketchContour} from '../../mesh/meshProxy/SketchContour';
import type {Viewer} from '../../viewer/Viewer';
import type {SketchTrimSlotDissolveResult} from '../../../types/sketch/trim/SketchTrimDimensionIntegrationTypes';
import type {SketchTrimCurveObject} from './SketchTrimTypes';

/**
 * 草图剪裁槽口拓扑服务。
 *
 * 职责：
 * 1. 仅处理 Trim 命令对槽口内部边造成的拓扑降级；
 * 2. 将原本作为闭合槽口成员的内部边恢复为普通草图边；
 * 3. 清理槽口旧轮廓，避免开链仍被当作闭合 profile。
 */
export class SketchTrimSlotTopologyService {
    /** 当前 Viewer。 */
    private readonly viewer: Viewer;

    /**
     * 构造草图剪裁槽口拓扑服务。
     * @param viewer 当前 Viewer。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    //------ 强力剪裁槽口开链降级 开始------
    /**
     * 若目标曲线属于槽口，则将槽口降级为普通草图边集合。
     *
     * 说明：
     * 1. SolidWorks 中槽口被剪开后不再是可上色闭合轮廓；
     * 2. PuppyCAD 的槽口是复合闭合对象，必须在修改内部边前解除闭合容器语义；
     * 3. 这里不执行实际裁剪，只负责拓扑容器迁移，保持 Trim 几何算法单一职责。
     *
     * @param curve 即将被 Trim 修改的曲线。
     * @returns 槽口运行时删除结果与持久拓扑变化。
     */
    public dissolveSlotForTrimTarget(curve: SketchTrimCurveObject): SketchTrimSlotDissolveResult {
        const slot = this.resolveSlotOwner(curve);
        if (!slot) {
            return {
                removedEntityIds: [],
                topologyChanges: [],
            };
        }

        const parent = slot.parent;
        const slotId = slot.uuid;
        const slotPersistentId = slot.persistentId;

        // 先清理旧轮廓，防止后续子边迁移期间仍被拾取为闭合区域。
        slot.clearContourDisplay();

        // 槽口边需要解除 closedCollection，后续选择与 profile 解析才能按普通开链处理。
        slot.getEdges().forEach((edge) => {
            edge.detachClosedCollection(slot);
        });

        // 槽口内部还可能包含构造中心线（直线/弧）等非边对象，同样需要解除闭合容器引用。
        this.collectSlotChildren(slot).forEach((child) => {
            this.detachClosedCollectionIfSupported(child, slot);
        });

        // 将槽口子对象迁回原草图组，避免目标边仍挂在已失效的闭合容器下。
        this.collectSlotChildren(slot).forEach((child) => {
            if (isSketchContour(child)) {
                return;
            }
            slot.remove(child);
            this.attachObjectToSketchContainer(child, parent);
        });

        // 子对象全部迁出后移除槽口容器本身，容器不再代表有效草图实体。
        if (slot.parent) {
            slot.parent.remove(slot);
        }

        return {
            removedEntityIds: [slotId],
            topologyChanges: [{
                sourceEntityId: slotPersistentId,
                targetEntityId: null,
                pointMappings: [],
                deletedPointIds: [],
            }],
        };
    }

    /**
     * 解析曲线所属槽口。
     * @param curve 待解析曲线。
     * @returns 所属槽口；没有所属槽口时返回 null。
     */
    private resolveSlotOwner(curve: SketchTrimCurveObject): SketchSlotObject | null {
        if (isSketchSlotObject(curve.parent)) {
            return curve.parent;
        }

        const closedCollection = Reflect.get(curve, 'closedCollection') as unknown;
        if (isSketchSlotObject(closedCollection)) {
            return closedCollection;
        }

        return null;
    }

    /**
     * 收集槽口当前子对象快照。
     * @param slot 槽口对象。
     * @returns 子对象快照。
     */
    private collectSlotChildren(slot: SketchSlotObject): THREE.Object3D[] {
        return [...slot.children];
    }

    /**
     * 若对象支持闭合容器解绑协议，则解除其槽口绑定。
     * @param object 待处理对象。
     * @param slot 需要解绑的槽口。
     */
    private detachClosedCollectionIfSupported(object: THREE.Object3D, slot: SketchSlotObject): void {
        const detachable = object as THREE.Object3D & {
            detachClosedCollection?: (geometry?: SketchSlotObject) => void;
        };
        if (typeof detachable.detachClosedCollection !== 'function') {
            return;
        }
        detachable.detachClosedCollection(slot);
    }

    /**
     * 将对象挂回草图容器。
     * @param object 待挂载对象。
     * @param parent 原槽口父容器。
     */
    private attachObjectToSketchContainer(object: THREE.Object3D, parent: THREE.Object3D | null): void {
        if (parent) {
            this.viewer.addObject(object, parent);
            return;
        }
        this.viewer.addSketchObject(object);
    }
    //------ 强力剪裁槽口开链降级 结束------
}
